const capabilities = [
  {
    title: "Scalable Systems",
    description: "Designing modular and maintainable architectures that grow with evolving technical demands and long-term operational requirements.",
    imageSeq: "cap-systems-05",
    imageQuery: "Abstract%20representation%20of%20scalable%20modular%20systems%20through%20layered%20geometric%20blocks%20and%20interconnected%20nodes%20in%20warm%20teal%20green%20tones%20on%20an%20off%20white%20background%2C%20engineering%20architecture%20aesthetic%20with%20clean%20precise%20lines%20and%20subtle%20depth%2C%20no%20text%20or%20human%20figures%2C%20minimal%20modern%20design",
    imageAlt: "Abstract layered geometric blocks representing scalable modular systems",
  },
  {
    title: "Performance Optimization",
    description: "Ensuring every component operates at peak efficiency through rigorous analysis, profiling, and refinement cycles.",
    imageSeq: "cap-perf-06",
    imageQuery: "Abstract%20pulse%20wave%20oscillation%20patterns%20rendered%20as%20flowing%20geometric%20waveforms%20in%20amber%20and%20golden%20tones%20on%20a%20warm%20cream%20background%2C%20representing%20performance%20optimization%20and%20system%20monitoring%2C%20clean%20minimal%20aesthetic%20without%20any%20text%20or%20human%20elements",
    imageAlt: "Abstract flowing waveforms representing performance optimization",
  },
  {
    title: "Quality Assurance",
    description: "Upholding rigorous standards at every stage of development to guarantee stability, reliability, and consistent technical excellence.",
    imageSeq: "cap-quality-07",
    imageQuery: "Abstract%20concentric%20circular%20target%20patterns%20with%20precise%20measurement%20markers%20in%20earthy%20olive%20and%20sage%20tones%20on%20a%20light%20sand%20background%2C%20representing%20precision%20engineering%20and%20quality%20standards%2C%20clean%20technical%20illustration%20style%20without%20text%20or%20human%20figures",
    imageAlt: "Abstract concentric target patterns representing precision quality standards",
  },
];

function CapabilitiesSection() {
  return (
    <section className="relative bg-background-50 py-20 md:py-28">
      <div className="w-full max-w-[1280px] mx-auto px-6 md:px-10 lg:px-16">

        {/* Top Copy Area */}
        <div className="flex flex-col lg:flex-row lg:items-start gap-6 md:gap-10 mb-14 md:mb-18">
          <div className="w-full lg:w-1/2">
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-foreground-950 leading-[1.15]">
              Core Engineering<br />Capabilities
            </h2>
          </div>
          <div className="w-full lg:w-1/2">
            <p className="text-base md:text-lg text-foreground-600 leading-relaxed font-light">
              Every solution is built on a foundation of modern software engineering practices, from initial architecture through sustained long-term maintenance and evolution.
            </p>
          </div>
        </div>

        {/* Three-Column Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {/* Left Card — Wider */}
          <div className="md:col-span-1 bg-background-100/80 rounded-2xl overflow-hidden flex flex-col">
            <div className="aspect-[4/3] overflow-hidden bg-background-200/50">
              <img
                src="https://readdy.ai/api/search-image?query=Abstract%20representation%20of%20scalable%20modular%20systems%20through%20layered%20geometric%20blocks%20and%20interconnected%20nodes%20in%20warm%20teal%20green%20tones%20on%20an%20off%20white%20background%2C%20engineering%20architecture%20aesthetic%20with%20clean%20precise%20lines%20and%20subtle%20depth%2C%20no%20text%20or%20human%20figures%2C%20minimal%20modern%20design&width=500&height=375&seq=cap-systems-05&orientation=landscape"
                alt="Abstract layered geometric blocks representing scalable modular systems"
                className="w-full h-full object-cover object-top"
                title="Scalable systems engineering architecture"
              />
            </div>
            <div className="p-6 md:p-7 flex flex-col gap-3 flex-1">
              <h3 className="font-heading text-lg md:text-xl font-semibold text-foreground-900">
                Scalable Systems
              </h3>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed font-light">
                Designing modular and maintainable architectures that grow with evolving technical demands and long-term operational requirements.
              </p>
            </div>
          </div>

          {/* Middle Card */}
          <div className="bg-background-100/80 rounded-2xl overflow-hidden flex flex-col">
            <div className="p-6 md:p-7 text-center">
              <h3 className="font-heading text-lg md:text-xl font-semibold text-foreground-900">
                Performance Optimization
              </h3>
            </div>
            <div className="aspect-[4/3] overflow-hidden bg-background-200/50">
              <img
                src="https://readdy.ai/api/search-image?query=Abstract%20pulse%20wave%20oscillation%20patterns%20rendered%20as%20flowing%20geometric%20waveforms%20in%20amber%20and%20golden%20tones%20on%20a%20warm%20cream%20background%2C%20representing%20performance%20optimization%20and%20system%20monitoring%2C%20clean%20minimal%20aesthetic%20without%20any%20text%20or%20human%20elements&width=400&height=300&seq=cap-perf-06&orientation=landscape"
                alt="Abstract flowing waveforms representing performance optimization"
                className="w-full h-full object-cover object-top"
                title="Performance optimization waveform patterns"
              />
            </div>
            <div className="p-6 md:p-7 flex flex-col gap-3 flex-1">
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed font-light">
                Ensuring every component operates at peak efficiency through rigorous analysis, profiling, and refinement cycles.
              </p>
            </div>
          </div>

          {/* Right Card */}
          <div className="bg-background-100/80 rounded-2xl overflow-hidden flex flex-col">
            <div className="p-6 md:p-7 text-center">
              <h3 className="font-heading text-lg md:text-xl font-semibold text-foreground-900">
                Quality Assurance
              </h3>
            </div>
            <div className="aspect-[4/3] overflow-hidden bg-background-200/50">
              <img
                src="https://readdy.ai/api/search-image?query=Abstract%20concentric%20circular%20target%20patterns%20with%20precise%20measurement%20markers%20in%20earthy%20olive%20and%20sage%20tones%20on%20a%20light%20sand%20background%2C%20representing%20precision%20engineering%20and%20quality%20standards%2C%20clean%20technical%20illustration%20style%20without%20text%20or%20human%20figures&width=400&height=300&seq=cap-quality-07&orientation=landscape"
                alt="Abstract concentric target patterns representing precision quality standards"
                className="w-full h-full object-cover object-top"
                title="Quality assurance precision engineering"
              />
            </div>
            <div className="p-6 md:p-7 flex flex-col gap-3 flex-1">
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed font-light">
                Upholding rigorous standards at every stage of development to guarantee stability, reliability, and consistent technical excellence.
              </p>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}

export default CapabilitiesSection;